import { useMemo } from 'react';
import { View, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';
import type { Trip } from '@/services/trips';
import { coerceDate, getInclusiveDayCount } from '@/utils/dateKeys';

type TripQuickStatsProps = {
  trip: Trip;
  noCard?: boolean;
  condensed?: boolean; // hide duplicated title/location/date in contexts with a hero
};

export function TripQuickStats({ trip, noCard = false, condensed = false }: TripQuickStatsProps) {
  const theme = useTheme();
  const startDate = coerceDate(trip.startDate);
  const endDate = coerceDate(trip.endDate);

  // Calculate days until trip or days elapsed
  const tripDates = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        daysUntil: 0,
        daysElapsed: 0,
        daysRemaining: 0,
        totalDays: 0,
        isUpcoming: false,
        isActive: false,
        isPast: false,
      };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const normalizedStart = new Date(startDate);
    normalizedStart.setHours(0, 0, 0, 0);
    
    const normalizedEnd = new Date(endDate);
    normalizedEnd.setHours(0, 0, 0, 0);
    
    const daysUntil = Math.ceil((normalizedStart.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((today.getTime() - normalizedStart.getTime()) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.ceil((normalizedEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const totalDays = getInclusiveDayCount(normalizedStart, normalizedEnd);
    
    const isUpcoming = today < normalizedStart;
    const isActive = today >= normalizedStart && today <= normalizedEnd;
    const isPast = today > normalizedEnd;
    
    return {
      daysUntil: Math.max(0, daysUntil),
      daysElapsed: Math.max(0, daysElapsed),
      daysRemaining: Math.max(0, daysRemaining),
      totalDays,
      isUpcoming,
      isActive,
      isPast,
    };
  }, [startDate, endDate]);

  // Get destination image
  const { imageUrl: destinationImage } = useLandmarkImage(trip.destination, trip.landmarkImage ?? null);

  // Format date range
  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return 'Dates TBD';
    }
    const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };

  const renderPrimaryRow = () => {
    if (condensed) {
      return (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          <Chip icon="calendar-outline" label={formatDateRange()} />
          {trip.destination && <Chip icon="location-outline" label={trip.destination} />}
          {tripDates.isUpcoming && <Chip icon="time-outline" label={`${tripDates.daysUntil || 0} days away`} />}
          {tripDates.isActive && <Chip icon="time-outline" label={`${tripDates.daysRemaining} left`} />}
          {tripDates.isPast && <Chip icon="archive-outline" label="Completed" />}
        </View>
      );
    }

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(2),
          position: 'relative',
          zIndex: 1,
        }}
      >
        {destinationImage && (
          <Image
            source={{ uri: destinationImage }}
            style={{
              width: 60,
              height: 60,
              borderRadius: theme.radii.md,
              backgroundColor: theme.colors.borderMuted,
            }}
            resizeMode="cover"
          />
        )}

        <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 16, flex: 1 }}>
              {trip.name}
            </ThemedText>
            {tripDates.isUpcoming && (
              <View
                style={{
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.25),
                  borderRadius: theme.radii.pill,
                  backgroundColor: theme.colors.primary + '20',
                }}
              >
                <Caption color={theme.colors.primary} style={{ fontSize: 10, fontWeight: '600' }}>
                  {tripDates.daysUntil === 0 ? 'Today' : tripDates.daysUntil === 1 ? '1 day' : `${tripDates.daysUntil} days`}
                </Caption>
              </View>
            )}
          </View>

          {trip.destination && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="location-outline" size={12} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }} numberOfLines={1}>
                {(() => {
                  if (trip.destinations && Array.isArray(trip.destinations) && trip.destinations.length > 1) {
                    return trip.destinations.map((d) => d.city).join(', ');
                  }
                  if (trip.destination.includes(';')) {
                    return trip.destination.split(';').map((d) => d.trim()).join(', ');
                  }
                  return trip.destination;
                })()}
              </Caption>
            </View>
          )}

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Ionicons name="calendar-outline" size={12} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
              {formatDateRange()}
            </Caption>
          </View>
        </View>
      </View>
    );
  };

  const content = (
    <View
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: noCard ? 0 : theme.radii.md,
        gap: theme.spacing(1.25),
      }}
    >
      {!noCard && !condensed && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.colors.primary + '08',
            opacity: 0.5,
          }}
        />
      )}

      {renderPrimaryRow()}

      {/* Stats Row */}
      <View
        style={{
          flexDirection: 'row',
          marginTop: theme.spacing(0.5),
          paddingTop: theme.spacing(1),
          borderTopWidth: 1,
          borderTopColor: theme.colors.borderMuted,
          gap: theme.spacing(2),
        }}
      >
        {tripDates.isActive && (
          <>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="checkmark-circle-outline" size={14} color={theme.colors.success} />
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  Day {Math.min(tripDates.daysElapsed + 1, tripDates.totalDays)}/{tripDates.totalDays}
                </Caption>
              </View>
            </View>
            <View style={{ flex: 1, alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  {tripDates.daysRemaining} left
                </Caption>
              </View>
            </View>
          </>
        )}
        {tripDates.isUpcoming && (
          <View style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="calendar-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                {tripDates.totalDays} days
              </Caption>
            </View>
          </View>
        )}
        {tripDates.isPast && (
          <View style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="archive-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                Completed
              </Caption>
            </View>
          </View>
        )}
      </View>
    </View>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card 
      variant="elevated" 
      style={{ 
        marginBottom: theme.spacing(2),
        overflow: 'hidden',
      }}
    >
      {content}
    </Card>
  );
}

function Chip({ icon, label }: { icon: string; label: string }) {
  const theme = useTheme();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        paddingHorizontal: theme.spacing(1),
        paddingVertical: theme.spacing(0.5),
        borderRadius: theme.radii.pill,
        backgroundColor: theme.colors.borderMuted + '25',
      }}
    >
      <Ionicons name={icon as any} size={12} color={theme.colors.textSecondary} />
      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '600' }}>
        {label}
      </Caption>
    </View>
  );
}

